import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  updateDoc,
  doc,
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Manages notification fetching, filtering, and mark-as-read.
 * Extracted from MainLayout.
 *
 * @param {string} userId
 */
export function useNotifications(userId) {
  const [notifications, setNotifications] = useState([]);
  const [hasUnread, setHasUnread] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc')
      );
      const snap = await getDocs(q);
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setNotifications(items);
      setHasUnread(items.some((n) => !n.read));
    } catch (err) {
      console.error('useNotifications: Error fetching:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAllRead = async () => {
    const unread = notifications.filter((n) => !n.read);
    for (const notif of unread) {
      const notifRef = doc(db, 'notifications', notif.id);
      await updateDoc(notifRef, { read: true });
    }
    setHasUnread(false);
    setNotifications(notifications.map((n) => (n.read ? n : { ...n, read: true })));
  };

  const getNotificationText = (notification) => {
    switch (notification.type) {
      case 'like':
        return `${notification.fromUsername} postunu beğendi.`;
      case 'comment':
        return `${notification.fromUsername} postuna yorum yaptı: "${notification.commentText || 'Yorum'}"`;
      case 'mention':
        return `${notification.fromUsername} seni bir postta etiketledi.`;
      case 'follow':
        return `${notification.fromUsername} seni takip etti.`;
      case 'badge':
        return `Yeni bir rozet kazandın: ${notification.badgeName}! 🏆`;
      case 'streak':
        return `${notification.streakDays} günlük streak hedefini başardın!`;
      case 'daily_task':
        return `Günlük görevini tamamladın: ${notification.taskName}!`;
      case 'goal_completed':
        return `Hedefini tamamladın: ${notification.goalTitle}!`;
      case 'study_milestone':
        return `${notification.studyHours} saat çalışma milestone'ına ulaştın!`;
      default:
        return `${notification.fromUsername} bir etkinlikte bulundu.`;
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      like: '❤️',
      comment: '💬',
      mention: '🏷️',
      follow: '👤',
      badge: '🏆',
      streak: '🔥',
      daily_task: '✅',
      goal_completed: '🎯',
      study_milestone: '⏰',
    };
    return icons[type] || '🔔';
  };

  return {
    notifications,
    hasUnread,
    loading,
    markAllRead,
    getNotificationText,
    getNotificationIcon,
    refresh: fetchNotifications,
  };
}
